import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import { getUserFromStorage } from '../components/storageUtility';
import {
  useCaseManagerActiveConsults,
  useCaseManagerPartnerFacilityFilters,
  useCaseManagerPastConsults,
} from '../../API/hooks/caseManagerHook';
import CustomTabs from '@ui-components/Tabs/CustomTabs';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import Searchicon from '@assets/images/search.svg';
import CustomTabPanel from '@ui-components/Tabs/CustomTabPanel';
import CustomTypography from '@qler-view/shared_lib/src/shared/lib/ui-components/Typography/CustomTypography';
import {
  CustomBadge,
  CustomCheckBoxGroup,
  CustomChip,
  CustomDivider,
  CustomLink,
  CustomMenu,
  CustomMenuList,
} from '@qler-view/shared_lib/src';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import FilterIcon from '@assets/images/Filter.svg';
import type {
  IndeterminateCheckboxFacilityProps,
  GeneralType,
  ConsultResponse,
  ConsultItem,
} from '../../API/services/caseManagerServices';
import { type Option } from '@qler-view/shared_lib/src/shared/lib/ui-components/CheckBoxGroup/CustomCheckBoxGroup';
import FilterCheckbox from './FilterCheckbox';

import { CallStatusEnum } from '../components/ConsultStatusChip';
import './consult.css';
import { FACILITY_CONSULT_CREATE_DASHBOARD_ROUTE_PATH } from '../../routers/RoutPath';
import { useLocation, useNavigate } from 'react-router-dom';
import ConsultListRender from './ConsultListRender';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { REFRESH_INTERVAL } from '@qler-view/shared_lib/src/shared/constants/CommanConstants';

const ConsultRequested = () => {
  const user = getUserFromStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const tabItems = [
    { label: 'Active Consults', value: 0 },
    { label: 'Past Consults', value: 1 },
  ];

  const lastSelectedTabIndex =
    localStorage.getItem('lastSelectedTabIndex') ?? '0';
  const [activeTab, setActiveTab] = useState<number | string>(
    Number.parseInt(lastSelectedTabIndex)
  );

  const {
    CaseManagerActiveConsults,
    isLoading: activeConsultsLoading,
    refetch,
  } = useCaseManagerActiveConsults(user?.userId ?? 0, {
    enabled: activeTab === 0,
    refetchInterval: activeTab === 0 ? REFRESH_INTERVAL : false,
  });

  const { CaseManagerPartnerFacilityFilters } =
    useCaseManagerPartnerFacilityFilters(user?.userId ?? 0);

  const [statusApplyButtonClicked, setStatusApplyButtonClicked] =
    useState<boolean>(false);
  const [appliedStatuses, setAppliedStatuses] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const statusApplyClicked = useRef<boolean>(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const checkedIdsStatusApply = useRef<string[]>([]);
  const [clearFilter, setClearFilter] = useState(false);
  const [facilityCount, setFacilityCount] = useState(0);
  const [openFacilities, setOpenFacilities] = useState(false);
  const [anchorElFacilities, setAnchorElFacilities] =
    useState<null | HTMLElement>(null);
  const checkedIdRef = useRef<IndeterminateCheckboxFacilityProps[]>([]);
  const [appliedFacilityCount, setAppliedFacilityCount] = useState<number>(0);
  const [searchFacilityKey, setSearchFacilityKey] = useState<string>('');
  const [searchConsults, setSearchConsults] = useState<string>('');
  const [filteredActiveConsults, setFilteredActiveConsults] =
    useState<ConsultResponse>();
  const [activeFilteredRequestByMeCount, setActiveFilteredRequestByMeCount] =
    useState<number>(0);
  const [
    activeFilteredRequestByOthersCount,
    setActiveFilteredRequestByOthersCount,
  ] = useState<number>(0);
  const [pastFilteredRequestByMeCount, setPastFilteredRequestByMeCount] =
    useState<number>(0);
  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const [
    pastFilteredRequestByOthersCount,
    setPastFilteredRequestByOthersCount,
  ] = useState<number>(0);

  const [filteredPastConsults, setFilteredPastConsults] =
    useState<ConsultResponse>();

  const getAriaControlsFacility = (isOpen: boolean) =>
    isOpen ? 'fade-menu-facilities' : undefined;

  const getAriaControls = (isOpen: boolean) =>
    isOpen ? 'fade-menu' : undefined;

  const getAriaExpand = (isOpen: boolean) => (isOpen ? 'true' : undefined);

  useEffect(() => {
    if (location?.state?.message) {
      setMessage('The consult request has been canceled.');
      setOpenToast(true);
    } else if (location?.state?.create) {
      setMessage('Consult created successfully!');
      setOpenToast(true);
    }
  }, [location?.state]);

  const mapStatusFiltersToOptions = (
    statuses: GeneralType[] | undefined
  ): Option[] => {
    return (
      statuses?.map((status) => ({
        label: status.name,
        value: status.id.toString(),
      })) ?? []
    );
  };

  const getStatusBasedOnTab = (tabIndex: string | number) => {
    return tabIndex === 0
      ? mapStatusFiltersToOptions(
          CaseManagerPartnerFacilityFilters?.activeStatusList
        )
      : mapStatusFiltersToOptions(
          CaseManagerPartnerFacilityFilters?.pastStatusList
        );
  };

  const getSelectedStatusNames = (
    selectedIds: string[],
    tabIndex: number | string
  ): string[] => {
    const statusList = getStatusBasedOnTab(tabIndex);

    if (!statusList) return [];

    return statusList
      .filter((status) => selectedIds.includes(status.value))
      .map((status) => status.label.toLowerCase());
  };

  const {
    CaseManagerPastConsults,
    isLoading: pastConsultsLoading,
    refetch: pastsRefetch,
  } = useCaseManagerPastConsults(user?.userId ?? 0, {
    enabled: activeTab === 1,
    refetchInterval: activeTab === 1 ? REFRESH_INTERVAL : false,
  });

  const handleTabChange = async (
    _event: React.SyntheticEvent,
    newValue: number | string
  ) => {
    localStorage.setItem('lastSelectedTabIndex', newValue.toString());
    setActiveTab(newValue);
    setSelectedStatuses([]);
    setStatusApplyButtonClicked(false);
    setAppliedStatuses([]);
    setSearchConsults('');
    if (newValue === 1) {
      const result = await pastsRefetch();
      setFilteredPastConsults(result.data);
    }
    searchAndFilterTodaysConsults([], checkedIdRef.current, false);
  };

  useEffect(() => {
    searchAndFilterTodaysConsults(
      selectedStatuses,
      checkedIdRef.current,
      false
    );
  }, [
    activeTab,
    searchConsults,
    CaseManagerActiveConsults,
    CaseManagerPastConsults,
    localStorage.getItem('lastSelectedTabIndex'),
  ]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (statusApplyClicked.current) {
      setSelectedStatuses((prevSelectedStatuses) => prevSelectedStatuses);
      statusApplyClicked.current = false;
    } else {
      setSelectedStatuses(checkedIdsStatusApply.current);
    }
  };

  const handleClickFacilities = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFacilities(event.currentTarget);
    setOpenFacilities(true);
    let count = 0;

    checkedIdRef.current?.forEach((facility) => {
      if (
        facility.checkFacility ||
        facility.facilities.some((facility) => facility.checked)
      ) {
        count++;
      }
    });

    setFacilityCount(count);
  };

  const handleCloseFacilities = () => {
    setAnchorElFacilities(null);
    setOpenFacilities(false);

    if (checkedIdRef.current.length === 0) {
      setFacilityCount(0);
    }

    setSearchFacilityKey('');
  };

  const getFacilityCountDisplay = (shouldClear: boolean, count: number) => {
    if (shouldClear) return 0;
    return count;
  };

  const handleFacilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFacilityKey(event.target.value);
  };

  const getFacilityCount = (data: IndeterminateCheckboxFacilityProps[]) => {
    let count = 0;
    data.forEach((facility) => {
      if (
        facility.checkFacility ||
        facility.facilities.some((hospital) => hospital.checked)
      ) {
        count++;
      }
    });

    setFacilityCount(count);
  };

  const searchTodaysConsults = (
    filteredConsults: ConsultResponse
  ): ConsultResponse => {
    if (searchConsults.trim().length > 0) {
      const searchTerms = searchConsults.toLowerCase().split(' ');

      const searchAvailableConsults =
        filteredConsults?.requestedByMe.filter((consult) => {
          const facilityName = consult.facilityName?.toLowerCase() ?? '';
          const partnerName = consult.partnerName?.toLowerCase() ?? '';
          const patientFirstName =
            consult.patientFirstName?.toLowerCase() ?? '';
          const patientLastName = consult.patientLastName?.toLowerCase() ?? '';
          const mrn = consult.mrn?.toLowerCase() ?? '';
          const reason = consult.reason?.toLowerCase() ?? '';
          const unitDepartment = consult.unitDepartment?.toLowerCase() ?? '';
          const fullName = `${patientFirstName} ${patientLastName}`;

          return (
            facilityName.includes(searchTerms.join(' ')) ||
            partnerName.includes(searchTerms.join(' ')) ||
            searchTerms.every(
              (term) =>
                patientFirstName.includes(term) ||
                patientLastName.includes(term)
            ) ||
            fullName.includes(searchTerms.join(' ')) ||
            mrn.includes(searchTerms.join(' ')) ||
            reason.includes(searchTerms.join(' ')) ||
            unitDepartment.includes(searchTerms.join(' '))
          );
        }) ?? [];

      const searchAssignedConsults =
        filteredConsults?.requestedByOthers.filter((consult) => {
          const facilityName = consult.facilityName?.toLowerCase() ?? '';
          const partnerName = consult.partnerName?.toLowerCase() ?? '';
          const patientFirstName =
            consult.patientFirstName?.toLowerCase() ?? '';
          const patientLastName = consult.patientLastName?.toLowerCase() ?? '';
          const mrn = consult.mrn?.toLowerCase() ?? '';
          const reason = consult.reason?.toLowerCase() ?? '';
          const unitDepartment = consult.unitDepartment?.toLowerCase() ?? '';
          const fullName = `${patientFirstName} ${patientLastName}`;

          return (
            facilityName.includes(searchTerms.join(' ')) ||
            partnerName.includes(searchTerms.join(' ')) ||
            searchTerms.every(
              (term) =>
                patientFirstName.includes(term) ||
                patientLastName.includes(term)
            ) ||
            fullName.includes(searchTerms.join(' ')) ||
            mrn.includes(searchTerms.join(' ')) ||
            reason.includes(searchTerms.join(' ')) ||
            unitDepartment.includes(searchTerms.join(' '))
          );
        }) ?? [];

      return {
        requestedByOthers: searchAssignedConsults,
        requestedByMe: searchAvailableConsults,
      } as ConsultResponse;
    } else {
      return structuredClone(CaseManagerActiveConsults) as ConsultResponse;
    }
  };

  const filterTodaysConsultsWithStatus = (
    consults: ConsultResponse,
    statuses: string[]
  ): ConsultResponse => {
    const filteredMyConsults =
      consults?.requestedByOthers.filter((consult) =>
        statuses.some(
          (status) =>
            consult.sessionStatus?.toLowerCase() === status.toLowerCase()
        )
      ) ?? [];

    const filteredAvailableConsults =
      consults?.requestedByMe.filter((consult) =>
        statuses.some(
          (status) =>
            consult.sessionStatus?.toLowerCase() === status.toLowerCase()
        )
      ) ?? [];

    return {
      requestedByOthers: filteredMyConsults,
      requestedByMe: filteredAvailableConsults,
    } as ConsultResponse;
  };

  const filterConsultsByFacility = (
    consultList: ConsultItem[] | undefined,
    hospitalName: string
  ) => {
    return (
      consultList?.filter((item) => item.facilityName === hospitalName) ?? []
    );
  };

  const filterAssignedConsultsList = (
    consults: ConsultResponse,
    hospitalName: string
  ) => {
    return filterConsultsByFacility(consults?.requestedByMe, hospitalName);
  };

  const filterAvailableConsultsList = (
    consults: ConsultResponse,
    hospitalName: string
  ) => {
    return filterConsultsByFacility(consults?.requestedByOthers, hospitalName);
  };

  const filterTodaysConsultsWithFacility = (
    consults: ConsultResponse,
    ids: IndeterminateCheckboxFacilityProps[]
  ): ConsultResponse => {
    const filteravailableConsults: ConsultItem[] = [];
    const filterassignedConsults: ConsultItem[] = [];
    if (facilityCount > 0) {
      ids.forEach((firstItem) => {
        if (firstItem.checkFacility) {
          const facilityName = firstItem.partnerName;

          const availableObjects =
            consults?.requestedByOthers.filter(
              (secondItem) => secondItem.partnerName === facilityName
            ) ?? [];

          filteravailableConsults.push(...availableObjects);
          const assignObjects =
            consults?.requestedByMe.filter(
              (secondItem) => secondItem.partnerName === facilityName
            ) ?? [];

          filterassignedConsults.push(...assignObjects);
        } else if (firstItem.checkInderterminate) {
          firstItem.facilities.forEach((element) => {
            const hospitalName = element.name;
            const hospitalChecked = element.checked;
            if (hospitalChecked) {
              const requestByOthersObjects = filterAvailableConsultsList(
                consults,
                hospitalName
              );
              filteravailableConsults.push(...requestByOthersObjects);

              const assignObjects = filterAssignedConsultsList(
                consults,
                hospitalName
              );
              filterassignedConsults.push(...assignObjects);
            }
          });
        }
      });

      return {
        requestedByOthers: filteravailableConsults,
        requestedByMe: filterassignedConsults,
      } as ConsultResponse;
    } else {
      return {
        requestedByOthers: consults?.requestedByOthers,
        requestedByMe: consults?.requestedByMe,
      } as ConsultResponse;
    }
  };

  const searchAndFilterTodaysConsults = (
    statuses: string[],
    ids: IndeterminateCheckboxFacilityProps[],
    isCompletedChecked: boolean
  ) => {
    const sourceData =
      activeTab === 0 ? CaseManagerActiveConsults : CaseManagerPastConsults;

    if (!sourceData) return;

    let filteredConsults: ConsultResponse;

    if (isCompletedChecked) {
      const filteredConsultsTemp: ConsultResponse = structuredClone(sourceData);
      const filteredMyConsults =
        filteredConsultsTemp?.requestedByOthers?.filter(
          (consult) =>
            consult.sessionStatus?.toLowerCase() !== CallStatusEnum.Completed &&
            consult.sessionStatus?.toLowerCase() !== CallStatusEnum.Cancelled &&
            consult.sessionStatus?.toLowerCase() !== CallStatusEnum.Abandoned
        ) ?? [];
      filteredConsultsTemp.requestedByOthers = filteredMyConsults;
      filteredConsults = filteredConsultsTemp;
    } else {
      filteredConsults = structuredClone(sourceData);
    }

    if (searchConsults !== '') {
      filteredConsults = searchTodaysConsults(filteredConsults);
    }

    if (statuses.length > 0) {
      const selectedStatusNames = getSelectedStatusNames(statuses, activeTab);
      filteredConsults = filterTodaysConsultsWithStatus(
        filteredConsults,
        selectedStatusNames
      );
    }

    if (ids.length > 0) {
      filteredConsults = filterTodaysConsultsWithFacility(
        filteredConsults,
        ids
      );
    }
    if (activeTab === 0) {
      setFilteredActiveConsults(filteredConsults);
      setActiveFilteredRequestByMeCount(filteredConsults.requestedByMe.length);
      setActiveFilteredRequestByOthersCount(
        filteredConsults.requestedByOthers.length
      );
    } else {
      setFilteredPastConsults(filteredConsults);
      setPastFilteredRequestByMeCount(filteredConsults.requestedByMe.length);
      setPastFilteredRequestByOthersCount(
        filteredConsults.requestedByOthers.length
      );
    }
  };

  const handleSearch = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchConsults(event.target.value);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-facility-list"
          successOrError={'success'}
          open={openToast}
          handleClose={() => setOpenToast(false)}
          toastrMessage={message}
        />
      )}
      <Grid2 container>
        <Grid2 size={{ xs: 6 }}>
          <Box className="flex">
            <Box>
              <CustomTabs
                id="tabs"
                tabs={tabItems}
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                orientation="horizontal"
              />
            </Box>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <Box className="flex float-right">
            <Box>
              <CustomTextField
                id="txt-search-consults"
                name="search-consults-qler"
                placeholder="Search Consults"
                size="small"
                type="text"
                value={searchConsults}
                onChange={handleSearch}
                isAnimated={true}
                startAdornment={
                  <CustomIconButton
                    id="search"
                    size="small"
                    icon={<Searchicon />}
                  />
                }
              />
            </Box>
            <Box className="ml-16 mr-16">
              {statusApplyButtonClicked ? (
                <CustomBadge
                  id="demo-badge"
                  badgeContent={appliedStatuses.length}
                  color="error"
                  max={600}
                >
                  <CustomChip
                    aria-controls={getAriaControls(open)}
                    aria-haspopup="true"
                    aria-expanded={getAriaExpand(open)}
                    onClick={(e) => {
                      handleClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Status"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              ) : (
                <CustomChip
                  aria-controls={getAriaControls(open)}
                  aria-haspopup="true"
                  aria-expanded={getAriaExpand(open)}
                  onClick={(e) => {
                    handleClick(e);
                  }}
                  className="box-border2"
                  id="demo-chip"
                  label="Status"
                  variant="outlined"
                  icon={<FilterIcon />}
                  iconPosition="left"
                />
              )}
              <CustomMenu
                id="conditional-menu"
                anchorEl={anchorEl ?? document.body}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                className="mt-4"
              >
                <Box className="status-filter-width p-20">
                  <Box className="flex-justify-space-between align-items-center">
                    <CustomTypography id="small-font" variant="subtitle1">
                      Status&nbsp;({selectedStatuses.length})
                    </CustomTypography>
                    <CustomLink
                      className="float-right"
                      color="primary"
                      underline="hover"
                      onClick={() => {
                        handleClose();
                        setSelectedStatuses([]);
                        setStatusApplyButtonClicked(false);
                        setAppliedStatuses([]);
                        searchAndFilterTodaysConsults(
                          [],
                          checkedIdRef.current,
                          false
                        );
                      }}
                      id="Status-Clear-Filter"
                    >
                      Clear Filter
                    </CustomLink>
                  </Box>
                  <Box className="pt-12 pb-10">
                    <CustomDivider />
                  </Box>
                  <CustomMenuList>
                    <CustomTypography
                      id="small-font"
                      variant="h6"
                      className="pb-12"
                    >
                      <CustomCheckBoxGroup
                        id="cbGroup"
                        options={getStatusBasedOnTab(activeTab) ?? []}
                        selectedValues={selectedStatuses}
                        onChange={setSelectedStatuses}
                        color="primary"
                        size="small"
                      />
                    </CustomTypography>
                  </CustomMenuList>
                  <Box>
                    <CustomButton
                      id="btnLarge"
                      variant="contained"
                      className="button-width"
                      onClick={() => {
                        setStatusApplyButtonClicked(true);
                        statusApplyClicked.current = true;
                        checkedIdsStatusApply.current = selectedStatuses;
                        setAppliedStatuses([...selectedStatuses]);
                        handleClose();
                        checkedIdsStatusApply.current = selectedStatuses;
                        searchAndFilterTodaysConsults(
                          selectedStatuses,
                          checkedIdRef.current,
                          false
                        );
                      }}
                    >
                      Apply
                    </CustomButton>
                  </Box>
                </Box>
              </CustomMenu>
            </Box>

            <Box>
              {clearFilter ? (
                <CustomChip
                  aria-controls={getAriaControlsFacility(openFacilities)}
                  aria-haspopup="true"
                  aria-expanded={getAriaExpand(openFacilities)}
                  onClick={(e) => {
                    handleClickFacilities(e);
                  }}
                  className="box-border2"
                  id="demo-chip-facilities"
                  label="Facilities"
                  icon={<FilterIcon />}
                  iconPosition="left"
                  variant="outlined"
                />
              ) : (
                <CustomBadge
                  id="demo-badge"
                  badgeContent={appliedFacilityCount}
                  color="error"
                  max={600}
                >
                  <CustomChip
                    aria-controls={getAriaControlsFacility(openFacilities)}
                    aria-haspopup="true"
                    aria-expanded={getAriaExpand(openFacilities)}
                    onClick={(e) => {
                      handleClickFacilities(e);
                    }}
                    className="box-border2"
                    id="demo-chip-facilities"
                    label="Facilities"
                    icon={<FilterIcon />}
                    iconPosition="left"
                    variant="outlined"
                  />
                </CustomBadge>
              )}
              <CustomMenu
                id="hospitallistmenu-menu"
                anchorEl={anchorElFacilities ?? document.body}
                open={openFacilities}
                onClose={handleCloseFacilities}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                className="mt-4"
              >
                <Box className="mr-20 ml-20 mt-16 mb-16">
                  <Box className="flex-justify-space-between align-items-center">
                    <CustomTypography
                      id="small-font"
                      variant="subtitle1"
                      className="pr-24"
                    >
                      Facilities&nbsp;(
                      {getFacilityCountDisplay(clearFilter, facilityCount)})
                    </CustomTypography>
                    <CustomLink
                      className="float-right"
                      color="primary"
                      underline="hover"
                      onClick={() => {
                        handleCloseFacilities();
                        setClearFilter(true);
                        checkedIdRef.current = [];
                        setAppliedFacilityCount(0);
                        searchAndFilterTodaysConsults(
                          selectedStatuses,
                          checkedIdRef.current,
                          false
                        );
                      }}
                      id="Facilities-Clear-Filter"
                    >
                      Clear Filter
                    </CustomLink>
                  </Box>
                  <Box className="pt-18">
                    <CustomDivider variant="fullWidth" />
                  </Box>
                  {CaseManagerPartnerFacilityFilters?.partnerFacilitiesMap && (
                    <Box className="pt-16">
                      <CustomTextField
                        id="txtSearchFacilityName"
                        placeholder="Search"
                        size="small"
                        type="text"
                        className="searchHeight"
                        value={searchFacilityKey}
                        onChange={handleFacilityChange}
                      />
                    </Box>
                  )}
                </Box>
                <CustomMenuList>
                  {CaseManagerPartnerFacilityFilters?.partnerFacilitiesMap && (
                    <FilterCheckbox
                      searchText={searchFacilityKey}
                      applyButtonCliked={(checkedIdsData) => {
                        checkedIdRef.current = checkedIdsData;
                        setClearFilter(false);
                        handleCloseFacilities();
                        setAppliedFacilityCount(facilityCount);
                        searchAndFilterTodaysConsults(
                          selectedStatuses,
                          checkedIdRef.current,
                          false
                        );
                      }}
                      facilityPartners={
                        CaseManagerPartnerFacilityFilters.partnerFacilitiesMap
                      }
                      clearFilter={clearFilter}
                      getFacilityPartnerCount={getFacilityCount}
                      storedPreviousValues={checkedIdRef.current}
                    />
                  )}
                </CustomMenuList>
              </CustomMenu>
            </Box>
            <Box className="ml-16">
              <CustomButton
                id="request-consult-button"
                variant="contained"
                size="large"
                className="add-consult-button"
                onClick={() => {
                  navigate(FACILITY_CONSULT_CREATE_DASHBOARD_ROUTE_PATH);
                }}
              >
                Add a Consult
              </CustomButton>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
      <Box>
        <CustomTabPanel value={activeTab} index={0}>
          <Box>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <Box className="text-align-left flex">
                  <CustomTypography
                    id="title-myconsults"
                    variant="h6"
                    color="textDisabled"
                  >
                    Reported By Me&nbsp;(
                    {activeFilteredRequestByMeCount})
                  </CustomTypography>
                </Box>
              </Grid2>
              <ConsultListRender
                consults={filteredActiveConsults?.requestedByMe ?? []}
                emptyMessage="No Consults Requested"
                isRequestedByOthers={false}
                loading={activeConsultsLoading || !filteredActiveConsults}
                onAccept={() => {
                  setActiveTab(0);
                  refetch();
                }}
              />
            </Grid2>
          </Box>

          <Box className="pt-24">
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <Box className="text-align-left">
                  <CustomTypography
                    id="title-myconsults"
                    variant="h6"
                    color="textDisabled"
                  >
                    Reported By Others&nbsp;(
                    {activeFilteredRequestByOthersCount})
                  </CustomTypography>
                </Box>
              </Grid2>
              <ConsultListRender
                consults={filteredActiveConsults?.requestedByOthers ?? []}
                emptyMessage="No Consults Requested By Others"
                isRequestedByOthers={true}
                loading={activeConsultsLoading || !filteredActiveConsults}
                onAccept={() => {
                  setActiveTab(0);
                  refetch();
                }}
              />
            </Grid2>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={activeTab} index={1}>
          <Box>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <Box className="text-align-left flex">
                  <CustomTypography
                    id="title-myconsults"
                    variant="h6"
                    color="textDisabled"
                  >
                    Reported By Me&nbsp;(
                    {pastFilteredRequestByMeCount})
                  </CustomTypography>
                </Box>
              </Grid2>
              <ConsultListRender
                consults={filteredPastConsults?.requestedByMe ?? []}
                emptyMessage="No my past consults"
                loading={pastConsultsLoading || !filteredPastConsults}
                pastConsult={true}
                isRequestedByOthers={true}
                onAccept={() => {
                  setActiveTab(1);
                  pastsRefetch();
                }}
              />
            </Grid2>
          </Box>
          <Box className="pt-24">
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <Box className="text-align-left">
                  <CustomTypography
                    id="title-myconsults"
                    variant="h6"
                    color="textDisabled"
                  >
                    Reported By Others&nbsp;(
                    {pastFilteredRequestByOthersCount})
                  </CustomTypography>
                </Box>
              </Grid2>
              <ConsultListRender
                consults={filteredPastConsults?.requestedByOthers ?? []}
                emptyMessage="No Consults Requested By Others"
                loading={pastConsultsLoading || !filteredPastConsults}
                pastConsult={true}
                isRequestedByOthers={true}
                onAccept={() => {
                  setActiveTab(1);
                  pastsRefetch();
                }}
              />
            </Grid2>
          </Box>
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default ConsultRequested;
